import React, { useState, useEffect } from 'react';
import { employeeAPI, uploadAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { DatePicker } from '../../components/ui/date-picker';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
  import {
  Plus,
  Search,
  Edit,
  UserX,
  UserCheck,
  Upload,
  Filter,
  Building2,
  X,
  FileText,
  Shield,
  LogOut,
  Mail,
  Users,
  Trash2,
  Download,
  Eye,
} from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ImageViewer } from '../../components/ui/image-viewer';
import { UploadChooser } from '../../components/ui/upload-chooser';
import { PasswordInput } from '../../components/ui/password-input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Department options
const DEPARTMENTS = [
  { value: 'spatial', label: 'Spatial' },
  { value: 'digital', label: 'Digital' },
  { value: 'ui_ux', label: 'UI / UX' },
  { value: 'tech', label: 'Tech' },
  { value: 'design', label: 'Design' },
  { value: 'admin', label: 'Admin' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'management', label: 'Management' },
];

const Employees = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formData, setFormData] = useState(getInitialFormData());
  const [uploading, setUploading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [viewerImage, setViewerImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('active');

  // Offboard modal
  const [offboardOpen, setOffboardOpen] = useState(false);
  const [offboardData, setOffboardData] = useState({ separation_status: 'resigned', separation_date: '', separation_reason: '' });
  const [offboarding, setOffboarding] = useState(false);

  // Resend invite modal
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteData, setInviteData] = useState({ new_email: '' });
  const [inviteSending, setInviteSending] = useState(false);

  // Delete employee
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  function getInitialFormData() {
    return {
      full_name: '',
      email: '',
      password: '',
      phone: '',
      designation: '',
      department: '',
      employment_type: 'full_time',
      date_of_joining: '',
      company: 'empact_studio',
      is_active: true,
      role: 'employee',
      profile_photo: '',
      // Documents
      aadhaar_card: '',
      aadhaar_front: '',
      aadhaar_back: '',
      pan_card: '',
      pan_url: '',
      // Addresses
      mumbai_address: '',
      permanent_address: '',
      // Emergency contact
      emergency_contact_name: '',
      emergency_contact_phone: '',
      emergency_contact_relation: '',
      // Freelancer fields
      skill_category: '',
      day_rate: '',
      project_rate: '',
      pan_or_gst: '',
      engagement_start_date: '',
      engagement_end_date: '',
      // Probation fields
      probation_status: 'permanent',
      probation_duration_months: '',
      // Gender
      gender: '',
      gender_other: '',
    };
  }

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await employeeAPI.getAll();
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (employee = null) => {
    if (employee) {
      setSelectedEmployee(employee);
      setFormData({
        full_name: employee.full_name || '',
        email: employee.email || '',
        password: '',
        phone: employee.phone || '',
        designation: employee.designation || '',
        department: employee.department || 'spatial',
        employment_type: employee.employment_type || 'full_time',
        date_of_joining: employee.date_of_joining || '',
        company: employee.company || 'empact_studio',
        is_active: employee.is_active !== false,
        role: employee.role || 'employee',
        profile_photo: employee.profile_photo || '',
        // Documents
        aadhaar_card: employee.aadhaar_card || '',
        aadhaar_front: employee.aadhaar_front || '',
        aadhaar_back: employee.aadhaar_back || '',
        pan_card: employee.pan_card || '',
        pan_url: employee.pan_url || '',
        // Addresses
        mumbai_address: employee.mumbai_address || '',
        permanent_address: employee.permanent_address || '',
        // Emergency contact
        emergency_contact_name: employee.emergency_contact_name || '',
        emergency_contact_phone: employee.emergency_contact_phone || '',
        emergency_contact_relation: employee.emergency_contact_relation || '',
        // Freelancer fields
        skill_category: employee.skill_category || '',
        day_rate: employee.day_rate || '',
        project_rate: employee.project_rate || '',
        pan_or_gst: employee.pan_or_gst || '',
        engagement_start_date: employee.engagement_start_date || '',
        engagement_end_date: employee.engagement_end_date || '',
        // Probation fields
        probation_status: employee.probation_status || 'permanent',
        probation_duration_months: employee.probation_duration_months || '',
        // Gender
        gender: employee.gender || '',
        gender_other: employee.gender_other || '',
      });
    } else {
      setSelectedEmployee(null);
      setFormData(getInitialFormData());
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEmployee(null);
    setFormData(getInitialFormData());
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const response = await uploadAPI.uploadPhoto(file);
      setFormData((prev) => ({ ...prev, profile_photo: response.data.url }));
    } catch (error) {
      console.error('Failed to upload photo:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDocUpload = async (file, fieldName) => {
    if (!file) return;
    setUploadingDoc(fieldName);
    try {
      const response = await uploadAPI.uploadDocument(file);
      setFormData((prev) => ({ ...prev, [fieldName]: response.data.url }));
    } catch (error) {
      console.error('Failed to upload document:', error);
      alert('Failed to upload document. Please try again.');
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const submitData = { ...formData };
      
      // Convert rates to numbers or null
      if (submitData.day_rate && submitData.day_rate !== '') {
        submitData.day_rate = parseFloat(submitData.day_rate);
      } else {
        submitData.day_rate = null;
      }
      if (submitData.project_rate && submitData.project_rate !== '') {
        submitData.project_rate = parseFloat(submitData.project_rate);
      } else {
        submitData.project_rate = null;
      }
      
      // Clean up empty strings to null for optional fields
      const optionalFields = [
        'phone', 'profile_photo', 'skill_category', 'pan_or_gst', 
        'engagement_start_date', 'engagement_end_date',
        'aadhaar_card', 'aadhaar_front', 'aadhaar_back', 'pan_card', 'pan_url', 'mumbai_address', 'permanent_address',
        'emergency_contact_name', 'emergency_contact_phone', 'emergency_contact_relation',
        'gender_other'
      ];
      optionalFields.forEach(field => {
        if (submitData[field] === '') {
          submitData[field] = null;
        }
      });

      // Handle probation duration
      if (submitData.probation_duration_months && submitData.probation_duration_months !== '') {
        submitData.probation_duration_months = parseInt(submitData.probation_duration_months);
      } else {
        submitData.probation_duration_months = null;
      }
      
      // Remove password if editing and empty
      if (selectedEmployee && !submitData.password) {
        delete submitData.password;
      }

      if (selectedEmployee) {
        await employeeAPI.update(selectedEmployee.id, submitData);
      } else {
        await employeeAPI.create(submitData);
      }

      await fetchEmployees();
      handleCloseModal();
    } catch (error) {
      console.error('Failed to save employee:', error);
      alert(error.response?.data?.detail || 'Failed to save employee');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (employee) => {
    if (employee.is_active) {
      // Open offboard modal instead of simple deactivate
      setSelectedEmployee(employee);
      setOffboardData({ separation_status: 'resigned', separation_date: new Date().toISOString().split('T')[0], separation_reason: '' });
      setOffboardOpen(true);
    } else {
      setSelectedEmployee(employee);
      setIsDeleteDialogOpen(true);
    }
  };

  const confirmToggleStatus = async () => {
    try {
      // Only used for re-activating now
      await employeeAPI.activate(selectedEmployee.id);
      await fetchEmployees();
    } catch (error) {
      console.error('Failed to activate:', error);
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedEmployee(null);
    }
  };

  const handleOffboard = async () => {
    if (!offboardData.separation_date) return;
    setOffboarding(true);
    try {
      await employeeAPI.offboard(selectedEmployee.id, offboardData);
      setOffboardOpen(false);
      setSelectedEmployee(null);
      await fetchEmployees();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to offboard employee');
    } finally {
      setOffboarding(false);
    }
  };

  const openResendInvite = (employee) => {
    setSelectedEmployee(employee);
    setInviteData({ new_email: employee.email });
    setInviteOpen(true);
  };

  const handleResendInvite = async () => {
    setInviteSending(true);
    try {
      const payload = {};
      if (inviteData.new_email && inviteData.new_email !== selectedEmployee.email) {
        payload.new_email = inviteData.new_email;
      }
      const res = await employeeAPI.resendInvite(selectedEmployee.id, payload);
      alert(res.data.message);
      setInviteOpen(false);
      setSelectedEmployee(null);
      await fetchEmployees();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviteSending(false);
    }
  };

  const openDeleteConfirm = (employee) => {
    setSelectedEmployee(employee);
    setDeleteOpen(true);
  };

  const handleDownloadPdf = async (employee) => {
    try {
      const res = await employeeAPI.downloadPdf(employee.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${employee.full_name.replace(/ /g, '_')}_Profile.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Failed to download profile');
    }
  };

  const handleDeleteEmployee = async () => {
    setDeleting(true);
    try {
      const res = await employeeAPI.delete(selectedEmployee.id);
      alert(res.data.message);
      setDeleteOpen(false);
      setSelectedEmployee(null);
      await fetchEmployees();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete employee');
    } finally {
      setDeleting(false);
    }
  };

  const filteredEmployees = employees.filter((emp) => {
    const matchesSearch =
      emp.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.designation.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCompany =
      filterCompany === 'all' ||
      emp.company === filterCompany ||
      (filterCompany !== 'both' && emp.company === 'both');
    
    const matchesStatus =
      activeTab === 'active' ? emp.is_active :
      activeTab === 'past' ? !emp.is_active : true;

    return matchesSearch && matchesCompany && matchesStatus;
  });

  const getCompanyBadge = (company) => {
    switch (company) {
      case 'empact_studio':
        return <span className="badge-empact">Empact Studio</span>;
      case 'event_resource':
        return <span className="badge-event">Event Resource</span>;
      case 'both':
        return <span className="badge-both">Both</span>;
      default:
        return null;
    }
  };

  const getTypeBadge = (type) => {
    const labels = {
      full_time: 'Full-time',
      part_time: 'Part-time',
      contract: 'Contract',
      freelancer: 'Freelancer',
    };
    const classes = {
      full_time: 'type-full-time',
      part_time: 'type-part-time',
      contract: 'type-contract',
      freelancer: 'type-freelancer',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${classes[type] || ''}`}>
        {labels[type] || type}
      </span>
    );
  };

  const getDepartmentLabel = (dept) => {
    if (!dept) return '';
    return dept.split(',').map(d => {
      const found = DEPARTMENTS.find(x => x.value === d.trim());
      return found ? found.label : d.trim();
    }).join(', ');
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
          <Shield className="w-3 h-3" />
          Admin
        </span>
      );
    }
    return null;
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith('http')) return photo;
    return `${BACKEND_URL}${photo}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D35A0]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-[#1D35A0]">Employee Directory</h2>
          <p className="text-slate-500 text-sm mt-1">
            Manage your team members across both companies
          </p>
        </div>
        <Button
          onClick={() => handleOpenModal()}
          className="bg-[#1D35A0] hover:bg-[#152A80] text-white"
          data-testid="add-employee-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Employee
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="employee-tabs">
          <TabsTrigger value="active" data-testid="tab-active">
            Active ({employees.filter(e => e.is_active).length})
          </TabsTrigger>
          <TabsTrigger value="past" data-testid="tab-past">
            Past Employees ({employees.filter(e => !e.is_active).length})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search by name, email, or designation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-3">
            <Select value={filterCompany} onValueChange={setFilterCompany}>
              <SelectTrigger className="w-40" data-testid="filter-company">
                <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Company" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                <SelectItem value="empact_studio">Empact Studio</SelectItem>
                <SelectItem value="event_resource">Event Resource</SelectItem>
                <SelectItem value="both">Both</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Employee List */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full" data-testid="employees-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Designation
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredEmployees.map((employee) => (
                <tr
                  key={employee.id}
                  className="hover:bg-slate-50 transition-colors"
                  data-testid={`employee-row-${employee.id}`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={getPhotoUrl(employee.profile_photo)} />
                        <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-sm">
                          {getInitials(employee.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{employee.full_name}</p>
                          {getRoleBadge(employee.role)}
                        </div>
                        <p className="text-sm text-slate-500">{employee.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-slate-900">{employee.designation}</p>
                    <p className="text-sm text-slate-500">{getDepartmentLabel(employee.department)}</p>
                  </td>
                  <td className="px-6 py-4">{getCompanyBadge(employee.company)}</td>
                  <td className="px-6 py-4">{getTypeBadge(employee.employment_type)}</td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      {employee.is_active ? (
                        <span className="status-active">Active</span>
                      ) : employee.separation_status ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                          {employee.separation_status === 'resigned' ? 'Resigned' : employee.separation_status === 'terminated' ? 'Terminated' : 'Contract Ended'}
                        </span>
                      ) : (
                        <span className="status-inactive">Inactive</span>
                      )}
                      {employee.separation_date && !employee.is_active && (
                        <span className="text-xs text-slate-400">Left: {employee.separation_date}</span>
                      )}
                      {employee.probation_status === 'probation' && employee.is_active && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700" data-testid={`probation-badge-${employee.id}`}>
                          Probation{employee.probation_duration_months ? ` (${employee.probation_duration_months}m)` : ''}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-1">
                      {employee.is_active && (
                        <>
                          {(employee.email !== 'vijay@eventresource.in' || user?.email === 'vijay@eventresource.in') && (
                            <Button variant="ghost" size="sm" onClick={() => handleOpenModal(employee)} data-testid={`edit-btn-${employee.id}`} title="Edit">
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(employee)} className="text-[#1D35A0] hover:text-[#152A80]" data-testid={`download-btn-${employee.id}`} title="Download Profile PDF">
                            <Download className="w-4 h-4" />
                          </Button>
                          {employee.email !== 'vijay@eventresource.in' && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => openResendInvite(employee)} className="text-blue-600 hover:text-blue-700" data-testid={`resend-btn-${employee.id}`} title="Resend Invite">
                                <Mail className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleToggleStatus(employee)} className="text-red-600 hover:text-red-700" data-testid={`offboard-btn-${employee.id}`} title="Offboard">
                                <LogOut className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => openDeleteConfirm(employee)} className="text-red-600 hover:text-red-700" data-testid={`delete-btn-${employee.id}`} title="Delete Permanently">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </>
                      )}
                      {!employee.is_active && (
                        <Button variant="ghost" size="sm" onClick={() => handleToggleStatus(employee)} className="text-green-600 hover:text-green-700" data-testid={`reactivate-btn-${employee.id}`} title="Re-activate">
                          <UserCheck className="w-4 h-4" />
                        </Button>
                      )}
                      {!employee.is_active && (
                        <Button variant="ghost" size="sm" onClick={() => openDeleteConfirm(employee)} className="text-red-600 hover:text-red-700" data-testid={`delete-inactive-btn-${employee.id}`} title="Delete Permanently">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden divide-y divide-slate-200" data-testid="employees-cards">
          {filteredEmployees.map((employee) => (
            <div key={employee.id} className="p-4" data-testid={`employee-card-${employee.id}`}>
              <div className="flex items-start gap-3">
                <Avatar className="w-12 h-9">
                  <AvatarImage src={getPhotoUrl(employee.profile_photo)} />
                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                    {getInitials(employee.full_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-slate-900">{employee.full_name}</p>
                      <p className="text-sm text-slate-500">{employee.designation}</p>
                    </div>
                    {employee.is_active ? (
                      <span className="status-active">Active</span>
                    ) : (
                      <span className="status-inactive">Inactive</span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {getCompanyBadge(employee.company)}
                    {getTypeBadge(employee.employment_type)}
                    {employee.probation_status === 'probation' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        Probation{employee.probation_duration_months ? ` (${employee.probation_duration_months}m)` : ''}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    {employee.is_active ? (
                      <>
                        <Button variant="outline" size="sm" onClick={() => handleOpenModal(employee)} className="flex-1">
                          <Edit className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openResendInvite(employee)} className="text-blue-600">
                          <Mail className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleToggleStatus(employee)} className="text-red-600">
                          <LogOut className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openDeleteConfirm(employee)} className="text-red-600" data-testid={`delete-card-btn-${employee.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                      <Button variant="outline" size="sm" onClick={() => handleToggleStatus(employee)} className="flex-1 text-green-600">
                        <UserCheck className="w-4 h-4 mr-1" /> Re-activate
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openDeleteConfirm(employee)} className="text-red-600" data-testid={`delete-inactive-card-btn-${employee.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredEmployees.length === 0 && (
          <div className="text-center py-12" data-testid="no-employees">
            <Users className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No employees found</p>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="employee-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D35A0]">
              {selectedEmployee ? 'Edit Employee' : 'Add New Employee'}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {selectedEmployee 
                ? 'Update the employee information below.' 
                : 'Fill in the details to add a new team member.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Photo Upload */}
            <div className="flex items-center gap-4">
              <Avatar className="w-20 h-20">
                <AvatarImage src={getPhotoUrl(formData.profile_photo)} />
                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-xl">
                  {getInitials(formData.full_name)}
                </AvatarFallback>
              </Avatar>
              <div>
                <UploadChooser onFile={(file) => handlePhotoUpload(file)} accept="image/*" disabled={uploading} testId="photo-upload-input">
                  <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer">
                    <Upload className="w-4 h-4" />
                    <span>{uploading ? 'Uploading...' : 'Upload Photo'}</span>
                  </div>
                </UploadChooser>
                {formData.profile_photo && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setFormData((prev) => ({ ...prev, profile_photo: '' }))}
                    className="text-red-600 mt-1"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                )}
              </div>
            </div>

            {/* Basic Info */}
            <div className="form-section">
              <h3 className="form-section-title">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, full_name: e.target.value }))}
                    required
                    data-testid="input-full-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                    required
                    data-testid="input-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">
                    {selectedEmployee ? 'New Password (leave blank to keep)' : 'Password *'}
                  </Label>
                  <PasswordInput
                    id="password"
                    value={formData.password}
                    onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                    required={!selectedEmployee}
                    data-testid="input-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                    data-testid="input-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gender">Gender *</Label>
                  <Select
                    value={formData.gender}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, gender: value, gender_other: value !== 'other' ? '' : prev.gender_other }))}
                  >
                    <SelectTrigger data-testid="select-gender">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.gender === 'other' && (
                  <div className="space-y-2">
                    <Label htmlFor="gender_other">Please specify *</Label>
                    <Input
                      id="gender_other"
                      value={formData.gender_other}
                      onChange={(e) => setFormData((prev) => ({ ...prev, gender_other: e.target.value }))}
                      placeholder="Specify gender"
                      required
                      data-testid="input-gender-other"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Work Info */}
            <div className="form-section">
              <h3 className="form-section-title">Work Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="designation">Designation *</Label>
                  <Input
                    id="designation"
                    value={formData.designation}
                    onChange={(e) => setFormData((prev) => ({ ...prev, designation: e.target.value }))}
                    required
                    data-testid="input-designation"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="department">Department * <span className="text-xs text-slate-400 font-normal">(select up to 2)</span></Label>
                  <div className="flex flex-wrap gap-2" data-testid="select-department">
                    {DEPARTMENTS.map((dept) => {
                      const selected = formData.department.split(',').filter(Boolean);
                      const isSelected = selected.includes(dept.value);
                      const atMax = selected.length >= 2 && !isSelected;
                      return (
                        <button
                          key={dept.value}
                          type="button"
                          disabled={atMax}
                          onClick={() => {
                            setFormData((prev) => {
                              const current = prev.department.split(',').filter(Boolean);
                              const updated = isSelected
                                ? current.filter((d) => d !== dept.value)
                                : [...current, dept.value];
                              return { ...prev, department: updated.join(',') };
                            });
                          }}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                            isSelected
                              ? 'bg-[#1D35A0] text-white border-[#1D35A0]'
                              : atMax
                              ? 'bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed'
                              : 'bg-white text-slate-700 border-slate-200 hover:border-[#1D35A0] hover:text-[#1D35A0]'
                          }`}
                          data-testid={`dept-chip-${dept.value}`}
                        >
                          {dept.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="employment_type">Employment Type *</Label>
                  <Select
                    value={formData.employment_type}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, employment_type: value }))}
                  >
                    <SelectTrigger data-testid="select-employment-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">Full-time</SelectItem>
                      <SelectItem value="part_time">Part-time</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                      <SelectItem value="freelancer">Freelancer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Company *</Label>
                  <Select
                    value={formData.company}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, company: value }))}
                  >
                    <SelectTrigger data-testid="select-company">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="empact_studio">Empact Studio</SelectItem>
                      <SelectItem value="event_resource">Event Resource</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date_of_joining">Date of Joining *</Label>
                  <DatePicker
                    value={formData.date_of_joining}
                    onChange={(v) => setFormData((prev) => ({ ...prev, date_of_joining: v }))}
                    placeholder="Pick date"
                    data-testid="input-date-of-joining"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role *</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, role: value }))}
                  >
                    <SelectTrigger data-testid="select-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="probation_status">Probation Status</Label>
                  <Select
                    value={formData.probation_status}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, probation_status: value, probation_duration_months: value === 'permanent' ? '' : prev.probation_duration_months }))}
                  >
                    <SelectTrigger data-testid="select-probation-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="permanent">Permanent</SelectItem>
                      <SelectItem value="probation">On Probation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.probation_status === 'probation' && (
                  <div className="space-y-2">
                    <Label htmlFor="probation_duration_months">Probation Duration (Months)</Label>
                    <Select
                      value={String(formData.probation_duration_months || '')}
                      onValueChange={(value) => setFormData((prev) => ({ ...prev, probation_duration_months: value }))}
                    >
                      <SelectTrigger data-testid="select-probation-duration">
                        <SelectValue placeholder="Select duration" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                          <SelectItem key={m} value={String(m)}>{m} {m === 1 ? 'Month' : 'Months'}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* Documents */}
            <div className="form-section">
              <h3 className="form-section-title">Documents</h3>
              <div className="space-y-4">
                {/* Aadhaar */}
                <div className="space-y-2">
                  <Label>Aadhaar Card</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Front Side</p>
                      {formData.aadhaar_front ? (
                        <div className="relative group">
                          <img src={getPhotoUrl(formData.aadhaar_front)} alt="Aadhaar Front" className="w-full h-28 object-cover rounded-lg border cursor-pointer" onClick={() => setViewerImage({ src: getPhotoUrl(formData.aadhaar_front), alt: 'Aadhaar Card - Front' })} />
                          <div className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button type="button" onClick={() => setViewerImage({ src: getPhotoUrl(formData.aadhaar_front), alt: 'Aadhaar Card - Front' })} className="p-2 bg-white/90 rounded-full hover:bg-white" title="View"><Eye className="w-4 h-4 text-slate-700" /></button>
                            <button type="button" onClick={() => setFormData((prev) => ({ ...prev, aadhaar_front: '' }))} className="p-2 bg-white/90 rounded-full hover:bg-white" title="Remove"><X className="w-4 h-4 text-red-600" /></button>
                          </div>
                        </div>
                      ) : (
                        <UploadChooser onFile={(file) => handleDocUpload(file, 'aadhaar_front')} disabled={uploadingDoc === 'aadhaar_front'} testId="aadhaar-front-upload-input">
                          <div className="flex flex-col items-center justify-center h-28 border-2 border-dashed border-slate-200 rounded-lg hover:border-[#1D35A0] hover:text-[#1D35A0] transition-colors text-slate-400 cursor-pointer">
                            <Upload className="w-5 h-5 mb-1" />
                            <span className="text-xs">{uploadingDoc === 'aadhaar_front' ? 'Uploading...' : 'Upload Front'}</span>
                          </div>
                        </UploadChooser>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Back Side</p>
                      {formData.aadhaar_back ? (
                        <div className="relative group">
                          <img src={getPhotoUrl(formData.aadhaar_back)} alt="Aadhaar Back" className="w-full h-28 object-cover rounded-lg border cursor-pointer" onClick={() => setViewerImage({ src: getPhotoUrl(formData.aadhaar_back), alt: 'Aadhaar Card - Back' })} />
                          <div className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button type="button" onClick={() => setViewerImage({ src: getPhotoUrl(formData.aadhaar_back), alt: 'Aadhaar Card - Back' })} className="p-2 bg-white/90 rounded-full hover:bg-white" title="View"><Eye className="w-4 h-4 text-slate-700" /></button>
                            <button type="button" onClick={() => setFormData((prev) => ({ ...prev, aadhaar_back: '' }))} className="p-2 bg-white/90 rounded-full hover:bg-white" title="Remove"><X className="w-4 h-4 text-red-600" /></button>
                          </div>
                        </div>
                      ) : (
                        <UploadChooser onFile={(file) => handleDocUpload(file, 'aadhaar_back')} disabled={uploadingDoc === 'aadhaar_back'} testId="aadhaar-back-upload-input">
                          <div className="flex flex-col items-center justify-center h-28 border-2 border-dashed border-slate-200 rounded-lg hover:border-[#1D35A0] hover:text-[#1D35A0] transition-colors text-slate-400 cursor-pointer">
                            <Upload className="w-5 h-5 mb-1" />
                            <span className="text-xs">{uploadingDoc === 'aadhaar_back' ? 'Uploading...' : 'Upload Back'}</span>
                          </div>
                        </UploadChooser>
                      )}
                    </div>
                  </div>
                </div>
                {/* PAN Card */}
                <div className="space-y-2">
                  <Label>PAN Card</Label>
                  {formData.pan_url ? (
                    <div className="relative group max-w-sm">
                      <img src={getPhotoUrl(formData.pan_url)} alt="PAN Card" className="w-full h-28 object-cover rounded-lg border cursor-pointer" onClick={() => setViewerImage({ src: getPhotoUrl(formData.pan_url), alt: 'PAN Card' })} />
                      <div className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button type="button" onClick={() => setViewerImage({ src: getPhotoUrl(formData.pan_url), alt: 'PAN Card' })} className="p-2 bg-white/90 rounded-full hover:bg-white" title="View"><Eye className="w-4 h-4 text-slate-700" /></button>
                        <button type="button" onClick={() => setFormData((prev) => ({ ...prev, pan_url: '' }))} className="p-2 bg-white/90 rounded-full hover:bg-white" title="Remove"><X className="w-4 h-4 text-red-600" /></button>
                      </div>
                    </div>
                  ) : (
                    <UploadChooser onFile={(file) => handleDocUpload(file, 'pan_url')} disabled={uploadingDoc === 'pan_url'} testId="pan-upload-input">
                      <div className="flex flex-col items-center justify-center h-28 border-2 border-dashed border-slate-200 rounded-lg hover:border-[#1D35A0] hover:text-[#1D35A0] transition-colors text-slate-400 cursor-pointer max-w-sm">
                        <Upload className="w-5 h-5 mb-1" />
                        <span className="text-xs">{uploadingDoc === 'pan_url' ? 'Uploading...' : 'Upload PAN'}</span>
                      </div>
                    </UploadChooser>
                  )}
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="form-section">
              <h3 className="form-section-title">Address Information</h3>
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="mumbai_address">Current Address</Label>
                  <Textarea
                    id="mumbai_address"
                    value={formData.mumbai_address}
                    onChange={(e) => setFormData((prev) => ({ ...prev, mumbai_address: e.target.value }))}
                    placeholder="Enter current address..."
                    rows={2}
                    data-testid="input-mumbai-address"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="permanent_address">Permanent Address</Label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.permanent_address && formData.mumbai_address && formData.permanent_address === formData.mumbai_address}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData((prev) => ({ ...prev, permanent_address: prev.mumbai_address }));
                          } else {
                            setFormData((prev) => ({ ...prev, permanent_address: '' }));
                          }
                        }}
                        className="rounded border-slate-300"
                        data-testid="same-address-checkbox"
                      />
                      <span className="text-xs text-slate-500">Same as current address</span>
                    </label>
                  </div>
                  <Textarea
                    id="permanent_address"
                    value={formData.permanent_address}
                    onChange={(e) => setFormData((prev) => ({ ...prev, permanent_address: e.target.value }))}
                    placeholder="Enter permanent address..."
                    rows={2}
                    data-testid="input-permanent-address"
                  />
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="form-section">
              <h3 className="form-section-title">Emergency Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="emergency_contact_name">Contact Name</Label>
                  <Input
                    id="emergency_contact_name"
                    value={formData.emergency_contact_name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, emergency_contact_name: e.target.value }))}
                    placeholder="Name"
                    data-testid="input-emergency-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emergency_contact_phone">Contact Phone</Label>
                  <Input
                    id="emergency_contact_phone"
                    value={formData.emergency_contact_phone}
                    onChange={(e) => setFormData((prev) => ({ ...prev, emergency_contact_phone: e.target.value }))}
                    placeholder="Phone number"
                    data-testid="input-emergency-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emergency_contact_relation">Relation</Label>
                  <Input
                    id="emergency_contact_relation"
                    value={formData.emergency_contact_relation}
                    onChange={(e) => setFormData((prev) => ({ ...prev, emergency_contact_relation: e.target.value }))}
                    placeholder="e.g., Father, Spouse"
                    data-testid="input-emergency-relation"
                  />
                </div>
              </div>
            </div>

            {/* Freelancer Fields */}
            {formData.employment_type === 'freelancer' && (
              <div className="form-section" data-testid="freelancer-fields-section">
                <h3 className="form-section-title">Freelancer Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="skill_category">Skill / Category</Label>
                    <Input
                      id="skill_category"
                      value={formData.skill_category}
                      onChange={(e) => setFormData((prev) => ({ ...prev, skill_category: e.target.value }))}
                      data-testid="input-skill-category"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pan_or_gst">PAN / GST Number</Label>
                    <Input
                      id="pan_or_gst"
                      value={formData.pan_or_gst}
                      onChange={(e) => setFormData((prev) => ({ ...prev, pan_or_gst: e.target.value }))}
                      data-testid="input-pan-gst"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="day_rate">Day Rate (INR)</Label>
                    <Input
                      id="day_rate"
                      type="number"
                      value={formData.day_rate}
                      onChange={(e) => setFormData((prev) => ({ ...prev, day_rate: e.target.value }))}
                      data-testid="input-day-rate"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="project_rate">Project Rate (INR)</Label>
                    <Input
                      id="project_rate"
                      type="number"
                      value={formData.project_rate}
                      onChange={(e) => setFormData((prev) => ({ ...prev, project_rate: e.target.value }))}
                      data-testid="input-project-rate"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="engagement_start_date">Engagement Start Date</Label>
                    <DatePicker
                      value={formData.engagement_start_date}
                      onChange={(v) => setFormData((prev) => ({ ...prev, engagement_start_date: v }))}
                      placeholder="Start date"
                      data-testid="input-engagement-start"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="engagement_end_date">Engagement End Date</Label>
                    <DatePicker
                      value={formData.engagement_end_date}
                      onChange={(v) => setFormData((prev) => ({ ...prev, engagement_end_date: v }))}
                      placeholder="End date"
                      data-testid="input-engagement-end"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={handleCloseModal} data-testid="cancel-btn">
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#1D35A0] hover:bg-[#152A80] text-white"
                disabled={saving}
                data-testid="save-employee-btn"
              >
                {saving ? 'Saving...' : selectedEmployee ? 'Update Employee' : 'Add Employee'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Re-activate Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent data-testid="toggle-status-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Re-activate Employee</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to re-activate {selectedEmployee?.full_name}? They will be able to log in again and their separation record will be cleared.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-toggle-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmToggleStatus}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-toggle-btn"
            >
              Re-activate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Offboard Modal */}
      <Dialog open={offboardOpen} onOpenChange={setOffboardOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <LogOut className="w-5 h-5" />
              Offboard {selectedEmployee?.full_name}
            </DialogTitle>
            <DialogDescription>
              This will disable their login access. All records (attendance, leave, expenses) will be preserved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Reason for Leaving *</Label>
              <Select value={offboardData.separation_status} onValueChange={(v) => setOffboardData(p => ({ ...p, separation_status: v }))}>
                <SelectTrigger data-testid="offboard-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="resigned">Resigned</SelectItem>
                  <SelectItem value="terminated">Terminated</SelectItem>
                  <SelectItem value="contract_ended">Contract Ended</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Last Working Date *</Label>
              <DatePicker
                value={offboardData.separation_date}
                onChange={(v) => setOffboardData(p => ({ ...p, separation_date: v }))}
                placeholder="Pick date"
                data-testid="offboard-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Any additional notes about the separation..."
                value={offboardData.separation_reason}
                onChange={(e) => setOffboardData(p => ({ ...p, separation_reason: e.target.value }))}
                data-testid="offboard-reason"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOffboardOpen(false)}>Cancel</Button>
              <Button
                onClick={handleOffboard}
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={offboarding || !offboardData.separation_date}
                data-testid="confirm-offboard-btn"
              >
                {offboarding ? 'Processing...' : 'Confirm Offboard'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Resend Invite Modal */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1D35A0]">
              <Mail className="w-5 h-5" />
              Resend Login Invite
            </DialogTitle>
            <DialogDescription>
              A new auto-generated password will be sent to {selectedEmployee?.full_name}. They'll be prompted to change it on login.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input
                type="email"
                value={inviteData.new_email}
                onChange={(e) => setInviteData(p => ({ ...p, new_email: e.target.value }))}
                placeholder={selectedEmployee?.email}
                data-testid="invite-email"
              />
              {inviteData.new_email && inviteData.new_email !== selectedEmployee?.email && (
                <p className="text-xs text-amber-600">Email will be updated to this new address</p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
              <Button
                onClick={handleResendInvite}
                className="bg-[#1D35A0] hover:bg-[#152A80] text-white"
                disabled={inviteSending}
                data-testid="send-invite-btn"
              >
                {inviteSending ? 'Sending...' : 'Send Invite'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="delete-employee-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700">Permanently Delete Employee</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete <strong>{selectedEmployee?.full_name}</strong>? This will remove all their data including attendance, leave, and expense records. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEmployee}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'Deleting...' : 'Delete Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Image Viewer Lightbox */}
      {viewerImage && (
        <ImageViewer src={viewerImage.src} alt={viewerImage.alt} onClose={() => setViewerImage(null)} />
      )}
    </div>
  );
};

export default Employees;
